import styles from "./GuestSelector.module.css";
import { ReactComponent as UsersIcon } from "../../../assets/icons/users.svg";

/**
 * Componente GuestSelector
 * Selector de huéspedes con contadores para adultos y niños
 *
 * @param {Object} props
 * @param {number} props.adults - Número de adultos
 * @param {number} props.children - Número de niños
 * @param {Array} props.childrenAges - Array de edades de niños { edad: number, unidad: string }
 * @param {function} props.onAdultsChange - Callback cuando cambia adultos
 * @param {function} props.onChildrenChange - Callback cuando cambia niños
 * @param {function} props.onChildAgeChange - Callback cuando cambia edad de niño (index, edad, unidad)
 * @param {number} props.maxAdults - Máximo de adultos
 * @param {number} props.maxChildren - Máximo de niños
 * @param {string} props.childrenAgesError - Mensaje de error para edades de niños
 * @param {boolean} props.darkMode - Modo oscuro
 */
const GuestSelector = ({
  adults = 1,
  children = 0,
  childrenAges = [],
  onAdultsChange,
  onChildrenChange,
  onChildAgeChange,
  maxAdults = 10,
  maxChildren = 10,
  childrenAgesError = "",
  darkMode = false,
}) => {
  // Función para verificar si falta la edad de un niño
  const isChildAgeMissing = (index) => {
    return !childrenAges[index] || !childrenAges[index].edad || childrenAges[index].edad === 0;
  };
  const handleAdultsIncrement = () => {
    if (adults < maxAdults) {
      onAdultsChange(adults + 1);
    }
  };

  const handleAdultsDecrement = () => {
    if (adults > 1) {
      onAdultsChange(adults - 1);
    }
  };

  const handleChildrenIncrement = () => {
    if (children < maxChildren) {
      onChildrenChange(children + 1);
    }
  };

  const handleChildrenDecrement = () => {
    if (children > 0) {
      onChildrenChange(children - 1);
    }
  };

  return (
    <div
      className={`${styles["guest-selector-container"]} ${
        darkMode ? styles.dark : ""
      }`}
    >
      {/* Header */}
      <div className={styles["guest-selector-header"]}>
        <UsersIcon className={styles["guest-selector-icon"]} />
        <h3 className={styles["guest-selector-title"]}>Integrantes</h3>
      </div>

      {/* Contador de Adultos */}
      <div className={styles["guest-counter-card"]}>
        <div className={styles["guest-counter-info"]}>
          <p className={styles["guest-counter-label"]}>Adultos</p>
          <p className={styles["guest-counter-description"]}>
            Mayores de 18 años
          </p>
        </div>
        <div className={styles["guest-counter-controls"]}>
          <button
            className={styles["guest-counter-button"]}
            onClick={handleAdultsDecrement}
            disabled={adults <= 1}
            aria-label="Disminuir adultos"
          >
            −
          </button>
          <span className={styles["guest-counter-value"]}>{adults}</span>
          <button
            className={`${styles["guest-counter-button"]} ${styles.primary}`}
            onClick={handleAdultsIncrement}
            disabled={adults >= maxAdults}
            aria-label="Aumentar adultos"
          >
            +
          </button>
        </div>
      </div>

      {/* Contador de Niños */}
      <div className={styles["guest-counter-card"]}>
        <div className={styles["guest-counter-info"]}>
          <p className={styles["guest-counter-label"]}>Niños</p>
          <p className={styles["guest-counter-description"]}>De 0 a 17 años</p>
        </div>
        <div className={styles["guest-counter-controls"]}>
          <button
            className={styles["guest-counter-button"]}
            onClick={handleChildrenDecrement}
            disabled={children <= 0}
            aria-label="Disminuir niños"
          >
            −
          </button>
          <span className={styles["guest-counter-value"]}>{children}</span>
          <button
            className={`${styles["guest-counter-button"]} ${styles.primary}`}
            onClick={handleChildrenIncrement}
            disabled={children >= maxChildren}
            aria-label="Aumentar niños"
          >
            +
          </button>
        </div>
      </div>

      {/* Edades de niños */}
      {children > 0 && (
        <div className={styles["children-ages-section"]}>
          {Array.from({ length: children }, (_, index) => {
            const currentAge = childrenAges[index];
            const maxAge = currentAge?.unidad === "meses" ? 11 : 17;
            const isAgeInvalid = currentAge?.edad > maxAge;
            const showError = (isChildAgeMissing(index) || isAgeInvalid) && childrenAgesError;
            
            return (
              <div key={index}>
                <label className={styles["child-age-label"]}>
                  Edad del niño {index + 1}
                  {showError && (
                    <span className={styles["required-indicator"]}> *</span>
                  )}
                </label>
                <div className={styles["child-age-input-wrapper"]}>
                  <input
                    type="number"
                    className={`${styles["child-age-input"]} ${
                      showError ? styles["error"] : ""
                    }`}
                    value={childrenAges[index]?.edad || ""}
                    onChange={(e) => {
                      let edad = parseInt(e.target.value) || 0;
                      const unidad = childrenAges[index]?.unidad || "años";
                      const max = unidad === "meses" ? 11 : 17;
                      
                      // Limitar el valor al máximo permitido
                      if (edad > max) {
                        edad = max;
                      }
                      
                      onChildAgeChange(index, edad, unidad);
                    }}
                    min={0}
                    max={maxAge}
                    placeholder="0"
                    required
                  />
                  <select
                    className={`${styles["child-age-select"]} ${
                      showError ? styles["error"] : ""
                    }`}
                    value={childrenAges[index]?.unidad || "años"}
                    onChange={(e) => {
                      const edad = childrenAges[index]?.edad || 0;
                      const newUnidad = e.target.value;
                      const max = newUnidad === "meses" ? 11 : 17;
                      
                      // Si la edad actual excede el nuevo máximo, ajustarla
                      const adjustedEdad = edad > max ? max : edad;
                      
                      onChildAgeChange(index, adjustedEdad, newUnidad);
                    }}
                  >
                    <option value="años">Años</option>
                    <option value="meses">Meses</option>
                  </select>
                </div>
                {isAgeInvalid && (
                  <p className={styles["child-age-individual-error"]}>
                    Máximo {maxAge} {currentAge?.unidad}
                  </p>
                )}
              </div>
            );
          })}
          {childrenAgesError && (
            <p className={styles["child-age-error-message"]}>
              {childrenAgesError}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GuestSelector;
